import { ImageUpload } from '@/components/shared/ImageUpload';
import { ImageGallery } from '@/components/shared/ImageGallery';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Loader2 } from 'lucide-react';
import type { ImageDto } from '@/components/shared/types';

export interface ImageManagementSectionProps {
  /** Array of images to display */
  images: ImageDto[];
  /** Whether images are loading */
  isLoading: boolean;
  /** Error from fetching images */
  fetchError?: string | null;
  /** Error from uploading images */
  uploadError?: string | null;
  /** Whether upload is in progress */
  isUploading: boolean;
  /** Handler for uploading images */
  onUpload: (files: File[]) => Promise<void>;
  /** Handler for deleting an image (if undefined, delete is disabled) */
  onDelete?: (imageId: number) => Promise<boolean | void>;
  /** Whether delete is allowed */
  canDelete?: boolean;
  /** Label for upload section (defaults to "Upload Images") */
  uploadLabel?: string;
  /** Label for gallery section (defaults to "Uploaded Images") */
  galleryLabel?: string;
  /** Maximum number of files per upload (defaults to 10) */
  maxFiles?: number;
  /** Maximum file size in MB (defaults to 10) */
  maxSizeMB?: number;
  /** Accepted file types (defaults to "image/*,application/pdf") */
  accept?: string;
}

/**
 * Reusable image/document management section
 * Combines ImageUpload and ImageGallery with loading and error states
 */
export function ImageManagementSection({
  images,
  isLoading,
  fetchError,
  uploadError,
  isUploading,
  onUpload,
  onDelete,
  canDelete = false,
  uploadLabel = 'Upload Images',
  galleryLabel = 'Uploaded Images',
  maxFiles = 10,
  maxSizeMB = 10,
  accept = 'image/*,application/pdf',
}: ImageManagementSectionProps) {
  const handleDelete = async (imageId: number) => {
    if (onDelete) {
      try {
        await onDelete(imageId);
      } catch {
        // Error is handled by the hook
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload section */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-gray-900">{uploadLabel}</h3>
        <ImageUpload
          onUpload={onUpload}
          isUploading={isUploading}
          maxFiles={maxFiles}
          maxSizeMB={maxSizeMB}
          accept={accept}
        />
        <ErrorAlert error={uploadError} className="mt-2" />
      </div>

      {/* Gallery section */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-gray-900">
          {galleryLabel} ({images.length})
        </h3>
        <ErrorAlert error={fetchError} className="mb-4" />
        <ImageGallery
          images={images}
          onDelete={canDelete ? handleDelete : undefined}
          canDelete={canDelete}
        />
      </div>
    </div>
  );
}
